import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Lock, ShieldCheck, Smartphone, Copy } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  loadCachedProgress, saveProgress, clearProgress,
  getDraftToken, clearDraftToken,
  saveDraftToServer, loadDraftFromServer, deleteDraftFromServer,
  buildResumeUrl,
} from "@/lib/wizardStorage";

import {
  STEPS, STEP_TAGLINES, STEP_CHEERS,
} from "@/components/signup/SignupShared";
import {
  Step1Identity, Step2Account, Step3Location, Step4Contact, Step5Review,
} from "@/components/signup/SignupSteps";
import SignupSuccess from "@/components/signup/SignupSuccess";
import { devLog } from "@/lib/devLog";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* Conversion-funnel tracker · best-effort, swallows all errors so a
   flaky network can never block the wizard. Failures are logged via
   devLog (no-op in prod) so debugging in dev still sees them. */
function fireFunnel(stage, sessionId) {
  try {
    const url = `${BACKEND || ""}/api/funnel/track`;
    const body = JSON.stringify({
      stage,
      session_id: sessionId || "",
      page_url: typeof window !== "undefined" ? window.location.pathname : "",
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
        .catch((e) => devLog.debug("[funnel] beacon fetch failed", e));
    }
  } catch (e) {
    devLog.debug("[funnel] track threw", e);
  }
}

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

/* -------- Step progress rail ------------------------------------ */
function ProgressRail({ current, onJump, t }) {
  const activeStep = STEPS.find((s) => s.id === current);
  const activeLabel = activeStep
    ? t(`signup.steps.${activeStep.id}.label`, activeStep.label)
    : "";
  return (
    <div className="mb-5">
      <div
        className="flex items-center w-full"
        data-testid="signup-progress-rail"
        role="list"
        aria-label={t("signup.progress.ariaLabel", "Signup progress")}
      >
        {STEPS.map((s, i) => {
          const done   = s.id < current;
          const active = s.id === current;
          const reachable = s.id <= current;
          const stepLabel = t(`signup.steps.${s.id}.label`, s.label);
          return (
            <div key={s.id} className="flex items-center shrink-0" role="listitem" style={i < STEPS.length - 1 ? { flex: "1 1 auto" } : undefined}>
              <button
                type="button"
                data-testid={`signup-progress-step-${s.id}`}
                aria-current={active ? "step" : undefined}
                aria-label={stepLabel}
                disabled={!reachable}
                onClick={() => reachable && !active && onJump?.(s.id)}
                className={[
                  "h-6 w-6 sm:h-7 sm:w-7 shrink-0 rounded-full grid place-items-center text-[12px] font-semibold transition-all duration-300",
                  active
                    ? "text-[color:var(--brand-ink)] scale-105"
                    : done
                    ? "text-[color:var(--brand)] cursor-pointer hover:scale-110"
                    : "text-[color:var(--wiz-text-3)] cursor-not-allowed",
                ].filter(Boolean).join(" ")}
                style={{
                  background: active
                    ? "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)"
                    : done
                    ? "color-mix(in srgb, var(--brand) 16%, transparent)"
                    : "color-mix(in srgb, var(--wiz-text-1) 4%, transparent)",
                  border: active
                    ? "1px solid color-mix(in srgb, var(--brand) 70%, transparent)"
                    : done
                    ? "1px solid color-mix(in srgb, var(--brand) 40%, transparent)"
                    : "1px solid var(--wiz-line)",
                  boxShadow: active
                    ? "0 0 0 4px color-mix(in srgb, var(--brand) 16%, transparent), 0 6px 16px -4px color-mix(in srgb, var(--brand) 45%, transparent)"
                    : undefined,
                }}
              >
                {done ? <Check size={12} strokeWidth={3} /> : s.id}
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="h-[2px] mx-1.5 sm:mx-2 rounded-full transition-colors duration-300"
                  style={{
                    flex: "1 1 auto",
                    minWidth: "8px",
                    background: done
                      ? "color-mix(in srgb, var(--brand) 75%, transparent)"
                      : "var(--wiz-line)",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      {activeStep && (
        <div className="mt-2 flex items-baseline gap-2 text-[12px]">
          <span className="font-mono uppercase tracking-[0.16em] text-[color:var(--wiz-text-3)]">
            {t("signup.progress.stepOf", "Step {{n}} / {{total}}", { n: activeStep.id, total: STEPS.length })}
          </span>
          <span className="font-semibold text-[color:var(--wiz-text-1)] truncate">
            {activeLabel}
          </span>
        </div>
      )}
    </div>
  );
}

/* -------- The wizard ------------------------------------------------------ */
export default function SignupWizard({ compact = false, onComplete = null, onStepChange = null }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const openedAt = useRef(0);
  useEffect(() => { openedAt.current = Date.now(); }, []);

  /* Conversion-funnel tracking · best-effort fire-and-forget. */
  const sessionIdRef = useRef("");
  useEffect(() => {
    try {
      const KEY = "ofin.funnel.session";
      let sid = localStorage.getItem(KEY);
      if (!sid) {
        sid = (crypto.randomUUID && crypto.randomUUID()) ||
              `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(KEY, sid);
      }
      sessionIdRef.current = sid;
    } catch (e) { devLog.debug("[wizard] funnel-session localStorage blocked", e); }
    fireFunnel("wizard_opened", sessionIdRef.current);
  }, []);

  const step2Fired = useRef(false);
  useEffect(() => {
    if (step >= 2 && !step2Fired.current) {
      step2Fired.current = true;
      fireFunnel("wizard_step_2", sessionIdRef.current);
    }
  }, [step]);

  useEffect(() => {
    if (typeof onStepChange === "function") onStepChange(step);
  }, [step, onStepChange]);

  const [form, setForm] = useState({
    firstname: "", lastname: "", title: "mr",
    email: "",     type: "Private", corporatename: "",
    country_id: "", city: "", address: "",
    phone_prefix_id: "", phonenumber: "",
    dob_year: "", dob_month: "", dob_day: "",
    agree_terms: true, agree_privacy: true, agree_risk: true, agree_marketing: true,
  });

  /* Resume offer · loaded once from sessionStorage. Non-PII fields only. */
  const [resumeOffer, setResumeOffer] = useState(() => loadCachedProgress());

  /* Cross-device share modal state. The modal shows the resume URL +
     a clipboard-copy button. The token comes from getDraftToken() or
     from saveDraftToServer() if the user clicks the button before a
     debounced save has fired. */
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  /* On mount · check the URL for a ?resume=TOKEN param. If present,
     fetch the full payload from the server and restore PII inclusive.
     This is the cross-device pickup path · the user pastes a sharable
     URL into their browser and lands here with form pre-filled. */
  const [resumingFromServer, setResumingFromServer] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search || "");
    const urlToken = params.get("resume");
    if (urlToken && urlToken.length >= 16) {
      setResumingFromServer(true);
      loadDraftFromServer(urlToken).then((draft) => {
        if (draft && draft.form) {
          setForm((f) => ({ ...f, ...draft.form }));
          setStep(Math.min(5, Math.max(1, draft.step || 1)));
          // Persist the token so subsequent saves overwrite the same
          // draft document.
          try { localStorage.setItem("ofin.signup.wizard.token", draft.token); }
          catch (e) { devLog.debug("[wizard] persisting draft token failed", e); }
          toast.success(t("signup.resume.toastWelcome", "Welcome back · resumed from your other device"), { duration: 3200 });
          setResumeOffer(null);
        } else {
          toast.message(t("signup.resume.toastExpired", "That resume link has expired · starting fresh."));
        }
        // Strip the ?resume= param from the URL so a page refresh
        // doesn't keep re-triggering the fetch.
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("resume");
          window.history.replaceState({}, "", u.toString());
        } catch (e) { devLog.debug("[wizard] history.replaceState blocked", e); }
      }).finally(() => setResumingFromServer(false));
    }
  }, []);

  /* Persist on every form/step change · two-tier persistence:
       (a) sessionStorage  · same-device · PII stripped (security)
       (b) server draft    · cross-device · full payload, 24h TTL
     Both writes are best-effort and debounced so back-to-back setForm
     calls don't thrash either store. */
  const saveTimer = useRef(null);
  useEffect(() => {
    const hasContent = (form.firstname || form.lastname || form.email || form.country_id || form.phonenumber);
    if (!hasContent) return;
    queueMicrotask(() => saveProgress({ form, step }));
    // Server save · debounced 700ms so a fast typist doesn't fire one
    // POST per keystroke. Skip while we're actively resuming from a
    // share URL · otherwise the empty pre-fetch state would clobber
    // the server payload we just downloaded.
    if (resumingFromServer) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveDraftToServer(form, step).catch(() => { /* best-effort */ });
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [form, step, resumingFromServer]);

  function applyResume() {
    if (!resumeOffer) return;
    // SECURITY · PII is never persisted; only non-PII preferences
    // survive the storage round-trip. Always rewind to Step 1 so the
    // user re-enters name/email/phone/address by hand.
    setForm((f) => ({ ...f, ...(resumeOffer.form || {}) }));
    setStep(1);
    setResumeOffer(null);
    toast.success(t("signup.toasts.welcomeBack", "Welcome back · your preferences are pre-filled ✨"), { duration: 2400 });
  }
  function dismissResume() {
    setResumeOffer(null);
    clearProgress();
    // Also wipe the server draft token so a future visit doesn't
    // resurrect old PII from the upstream draft document.
    const tk = getDraftToken();
    if (tk) {
      deleteDraftFromServer(tk).catch(() => {});
      clearDraftToken();
    }
  }

  /* Cross-device "continue on another device" share. Ensures a server
     draft exists, then opens a small modal with the resume URL +
     clipboard-copy button. */
  async function openShareModal() {
    let tk = getDraftToken();
    const hasContent = (form.firstname || form.lastname || form.email || form.country_id || form.phonenumber);
    if (!tk && hasContent) {
      // No saved token yet · save now and capture the issued one.
      tk = await saveDraftToServer(form, step);
    }
    if (!tk) {
      toast.message(t("signup.toasts.fillFirst", "Fill in any field first · we'll generate the resume link."));
      return;
    }
    setShareUrl(buildResumeUrl(tk));
    setShareModalOpen(true);
  }
  async function copyShareUrl() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("signup.toasts.linkCopied", "Link copied · paste it on your other device to continue."), { duration: 3000 });
    } catch (_) {
      toast.message(t("signup.toasts.clipFail", "Couldn't access clipboard · select the URL and copy it manually."));
    }
  }

  /* Google OAuth popup callback · skips to Step 3 after pre-fill. */
  useEffect(() => {
    fetch(`${BACKEND}/api/signup/countries`)
      .then((r) => r.json())
      .then((d) => setCountries(d.countries || []))
      .catch(() => toast.error(t("signup.toasts.countryLoadFail", "Couldn't load country list. Please refresh.")));
  }, []);

  /* IP geolocation auto-fill · runs once on mount. We pre-fill the
     country, city and phone prefix from the visitor's IP so they
     don't have to type information we can already infer. We NEVER
     overwrite a field the user has already typed into · and we only
     touch the form once `countries` has loaded (otherwise the country
     dropdown would render before its selected option exists). */
  const geoRanRef = useRef(false);
  useEffect(() => {
    if (geoRanRef.current) return;
    if (countries.length === 0) return; // wait for the country list
    geoRanRef.current = true;
    fetch(`${BACKEND}/api/signup/geo`)
      .then((r) => r.ok ? r.json() : null)
      .then((g) => {
        if (!g || !g.ok) return;
        setForm((f) => ({
          ...f,
          country_id:      f.country_id      || (g.country_id      ? String(g.country_id)      : ""),
          phone_prefix_id: f.phone_prefix_id || (g.phone_prefix_id ? String(g.phone_prefix_id) : ""),
          city:            f.city            || g.city || "",
        }));
      })
      .catch(() => { /* best-effort · no toast on failure */ });
  }, [countries.length]);

  const pickedCountry = useMemo(
    () => countries.find((c) => String(c.id) === String(form.country_id)),
    [countries, form.country_id],
  );
  const pickedPrefix = useMemo(
    () => countries.find((c) => String(c.id) === String(form.phone_prefix_id)),
    [countries, form.phone_prefix_id],
  );

  function validate(stepId) {
    const e = {};
    if (stepId >= 1) {
      if (!form.firstname.trim()) e.firstname = t("signup.toasts.errFirstname", "First name is required");
      if (!form.lastname.trim())  e.lastname  = t("signup.toasts.errLastname",  "Last name is required");
      if (!form.title)            e.title     = t("signup.toasts.errTitle",     "Pick a title");
    }
    if (stepId >= 2) {
      if (!isEmail(form.email))            e.email = t("signup.toasts.errEmail", "Enter a valid email");
      if (form.type === "Corporate" && !form.corporatename.trim())
        e.corporatename = t("signup.toasts.errCorpName", "Company name is required for corporate accounts");
    }
    if (stepId >= 3) {
      if (!form.country_id) e.country_id = t("signup.toasts.errCountry", "Select a country");
      if (!form.city.trim())    e.city     = t("signup.toasts.errCity",    "City is required");
      if (!form.address.trim()) e.address  = t("signup.toasts.errAddress", "Address is required");
    }
    if (stepId >= 4) {
      if (!form.phone_prefix_id)       e.phone_prefix_id = t("signup.toasts.errPrefix", "Pick a prefix");
      if (!String(form.phonenumber).replace(/\D/g, "")) e.phonenumber = t("signup.toasts.errPhone", "Phone number is required");
      const y = Number(form.dob_year), m = Number(form.dob_month), d = Number(form.dob_day);
      if (!y || !m || !d) e.dob = t("signup.toasts.errDob", "Date of birth is required");
      else {
        const dob = new Date(y, m - 1, d);
        const cutoff = new Date(); cutoff.setFullYear(cutoff.getFullYear() - 18);
        if (dob > cutoff) e.dob = t("signup.toasts.err18", "You must be at least 18");
      }
    }
    if (stepId >= 5) {
      if (!form.agree_terms || !form.agree_privacy || !form.agree_risk)
        e.consents = t("signup.toasts.errConsents", "Please accept the legal documents to continue");
    }
    return e;
  }

  function nextStep() {
    const e = validate(step);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error(t("signup.toasts.toastFix", "Almost — just need to fix the highlighted fields."));
      return;
    }
    const cheer = STEP_CHEERS[step];
    if (cheer) {
      const msg = step === 1 ? `${cheer}${form.firstname.trim()} 👋` : cheer;
      toast.success(msg, { duration: 1800 });
    }
    setStep((s) => Math.min(5, s + 1));
  }
  function prevStep() { setStep((s) => Math.max(1, s - 1)); }
  function jumpToStep(target) { if (target < step) setStep(target); }
  function jumpFromReview(target) { setStep(target); }

  async function handleSubmit() {
    const all = validate(5);
    setErrors(all);
    if (Object.keys(all).length) {
      toast.error(t("signup.toasts.toastReview", "Please review the highlighted fields."));
      return;
    }
    const elapsed = (Date.now() - openedAt.current) / 1000;
    if (elapsed < 10) {
      const remaining = Math.ceil(10 - elapsed);
      toast.message(remaining === 1
        ? t("signup.toasts.almostSecond",  "Almost there — give us {{n}} more second to verify the form…",  { n: remaining })
        : t("signup.toasts.almostSeconds", "Almost there — give us {{n}} more seconds to verify the form…", { n: remaining })
      );
      await new Promise((r) => setTimeout(r, (10 - elapsed) * 1000));
    }
    setSubmitting(true);
    fireFunnel("wizard_submitted", sessionIdRef.current);
    try {
      const payload = {
        firstname:      form.firstname.trim(),
        lastname:       form.lastname.trim(),
        title:          form.title,
        email:          form.email.trim().toLowerCase(),
        type:           form.type,
        corporatename:  form.type === "Corporate" ? form.corporatename.trim() : "",
        country_id:     Number(form.country_id),
        phone_prefix_id: Number(form.phone_prefix_id || form.country_id),
        phonenumber:    form.phonenumber.replace(/\D/g, ""),
        address:        form.address.trim(),
        city:           form.city.trim(),
        dob_year:       Number(form.dob_year),
        dob_month:      Number(form.dob_month),
        dob_day:        Number(form.dob_day),
        page_url:       window.location.href,
        // Server-side draft id · the register endpoint deletes the
        // draft document on a successful CRM lead create.
        draft_token:    getDraftToken() || "",
      };
      const r = await fetch(`${BACKEND}/api/signup/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (data?.ok) {
        // Wipe both tiers on success · we never want to offer to
        // "resume" a completed signup. The server draft is also
        // deleted by the /signup/register handler.
        clearProgress();
        const tk = getDraftToken();
        if (tk) {
          deleteDraftFromServer(tk).catch(() => {});
          clearDraftToken();
        }
        setSuccess({ email: payload.email, name: form.firstname.trim(), message: data.message });
        if (window.gtag) {
          try { window.gtag("event", "sign_up", { method: "wizard" }); }
          catch (e) { devLog.debug("[wizard] gtag sign_up event failed", e); }
        }
      } else {
        const fieldErrs = data?.errors || {};
        const mapped = {};
        if (fieldErrs.firstname)  mapped.firstname  = fieldErrs.firstname;
        if (fieldErrs.lastname)   mapped.lastname   = fieldErrs.lastname;
        if (fieldErrs.email)      mapped.email      = fieldErrs.email;
        if (fieldErrs.country_id) mapped.country_id = fieldErrs.country_id;
        if (fieldErrs.phonenumber) mapped.phonenumber = fieldErrs.phonenumber;
        if (Object.keys(mapped).length) setErrors(mapped);
        toast.error(data?.message || t("signup.errors.generic", "We couldn't complete your registration."));
        if (mapped.firstname || mapped.lastname) setStep(1);
        else if (mapped.email)                   setStep(2);
        else if (mapped.country_id)              setStep(3);
        else if (mapped.phonenumber)             setStep(4);
      }
    } catch (err) {
      toast.error(t("signup.errors.network", "Network error. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  /* ─── Success page · the celebration moment ── */
  if (success) {
    return <SignupSuccess success={success} compact={compact} onComplete={onComplete} />;
  }

  const currentStep = STEPS[step - 1];

  /* ─── Wizard shell ── */
  const wizardBody = (
    <main
      className={
        compact
          ? "px-0 pt-0 pb-1"
          : "max-w-3xl mx-auto px-5 pt-10 pb-20 signup-wizard-skin"
      }
    >
      {/* Header · pill + headline + per-step tagline. */}
      <div className="mb-5 pr-12">
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-3"
          style={{
            background: "color-mix(in srgb, var(--brand) 12%, transparent)",
            border: "1px solid color-mix(in srgb, var(--brand) 40%, transparent)",
          }}
        >
          <Sparkles size={11} className="text-brand" />
          <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[color:var(--brand)]">
            {t("signup.pill", "Open a live account")}
          </span>
        </span>
        <h1 className={
          compact
            ? "mt-3 text-[24px] sm:text-[28px] font-semibold tracking-tight text-[color:var(--wiz-text-1)] leading-[1.1]"
            : "mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-[color:var(--wiz-text-1)]"
        }>
          {t("signup.headline", "Let's get you trading.")}
        </h1>
        <p className="mt-2 text-[12.5px] sm:text-[13px] leading-relaxed text-[color:var(--wiz-text-2)] max-w-md">
          {t(`signup.taglines.${step}`, STEP_TAGLINES[step])}
        </p>
      </div>

      {/* Resume-progress banner · stacks vertically on mobile so the title /
          body / dual-button cluster never gets crushed. Icon + title sit
          on a single baseline; body wraps beneath; two equal-width buttons
          fill the bottom row via grid-cols-2. */}
      {resumeOffer && (
        <div
          className="mb-5 rounded-xl p-4"
          style={{
            background: "color-mix(in srgb, var(--brand) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
          }}
          data-testid="signup-resume-banner"
        >
          <div className="flex items-start gap-3">
            <div
              className="h-9 w-9 rounded-full grid place-items-center shrink-0"
              style={{
                background: "color-mix(in srgb, var(--brand) 16%, transparent)",
                border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
              }}
              aria-hidden="true"
            >
              <Sparkles size={14} className="text-[color:var(--brand)]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-[color:var(--wiz-text-1)] leading-tight">
                {t("signup.resume.title", "Welcome back")}
              </div>
              <div className="text-[12.5px] text-[color:var(--wiz-text-2)] mt-1 leading-snug">
                {t("signup.resume.body", "We've saved your country, account type and date of birth. Personal details aren't stored.")}
              </div>
            </div>
          </div>
          <div className="mt-3.5 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={dismissResume}
              data-testid="signup-resume-dismiss"
              className="btn-gloss-ghost h-10 rounded-lg text-[13px] font-medium"
              style={{ color: "var(--wiz-text-2)" }}
            >
              {t("signup.resume.dismiss", "Start fresh")}
            </button>
            <button
              type="button"
              onClick={applyResume}
              data-testid="signup-resume-apply"
              className="btn-gloss h-10 rounded-lg text-[13px] font-semibold"
              style={{
                background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
                color: "var(--brand-ink)",
                "--gloss-accent": "var(--brand-deep)",
              }}
            >
              {t("signup.resume.apply", "Resume")}
            </button>
          </div>
        </div>
      )}

      <ProgressRail current={step} onJump={jumpToStep} t={t} />

      <div className="wiz-step-card p-5 sm:p-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <Step1Identity
              form={form}
              setForm={setForm}
              errors={errors}
            />
          )}
          {step === 2 && (
            <Step2Account
              form={form}
              setForm={setForm}
              errors={errors}
            />
          )}
          {step === 3 && (
            <Step3Location
              form={form}
              setForm={setForm}
              errors={errors}
              countries={countries}
            />
          )}
          {step === 4 && (
            <Step4Contact
              form={form}
              setForm={setForm}
              errors={errors}
              countries={countries}
              pickedPrefix={pickedPrefix}
            />
          )}
          {step === 5 && (
            <Step5Review
              form={form}
              setForm={setForm}
              errors={errors}
              pickedCountry={pickedCountry}
              pickedPrefix={pickedPrefix}
              onJumpToStep={jumpFromReview}
            />
          )}
        </AnimatePresence>

        {/* ─── Footer nav · Back / hint / Continue/Submit ── */}
        <div className="mt-6 pt-4 flex items-center justify-between border-t" style={{ borderColor: "var(--wiz-line)" }}>
          <Button
            type="button"
            variant="ghost"
            onClick={prevStep}
            disabled={step === 1 || submitting}
            data-testid="signup-back"
            className="gap-1 text-[color:var(--wiz-text-2)] hover:text-[color:var(--wiz-text-1)] hover:bg-[color:color-mix(in_srgb,var(--wiz-text-1)_6%,transparent)] disabled:opacity-40"
          >
            <ArrowLeft size={14} /> {t("signup.nav.back", "Back")}
          </Button>
          <div className="text-[12px] text-[color:var(--wiz-text-3)] hidden sm:block font-medium">
            {t(`signup.steps.${step}.hint`, currentStep.hint)}
          </div>
          {step < 5 ? (
            <Button type="button" onClick={nextStep} data-testid="signup-next" className="gap-1.5 font-semibold">
              {t("signup.nav.continue", "Continue")} <ArrowRight size={14} />
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={submitting} data-testid="signup-submit" className="gap-2 min-w-[180px] font-semibold">
              {submitting
                ? <><Loader2 size={14} className="animate-spin" /> {t("signup.nav.submitting", "Creating account…")}</>
                : <>{t("signup.nav.submit", "Open my account")} <ArrowRight size={14} /></>}
            </Button>
          )}
        </div>

        {/* Trust strip · lives inside the step card */}
        <div
          className="mt-5 pt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-[color:var(--wiz-text-2)] border-t"
          style={{ borderColor: "var(--wiz-line)" }}
        >
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Lock size={13} className="text-[color:var(--brand)]" />
            {t("signup.trust.encryption", "Bank-grade encryption")}
          </span>
          <span className="hidden sm:inline-block h-1 w-1 rounded-full" style={{ background: "var(--wiz-line-strong)" }} />
          <span className="inline-flex items-center gap-1.5 font-medium">
            <ShieldCheck size={13} className="text-[color:var(--brand)]" />
            {t("signup.trust.funds", "Segregated client funds")}
          </span>
          <span className="hidden sm:inline-block h-1 w-1 rounded-full" style={{ background: "var(--wiz-line-strong)" }} />
          {/* Cross-device pickup · opens a share modal with the
              resume URL. Only renders once the user has typed
              something so we never offer to share an empty draft. */}
          <button
            type="button"
            onClick={openShareModal}
            data-testid="signup-share-device"
            className="inline-flex items-center gap-1.5 font-medium text-[color:var(--brand)] hover:text-[color:var(--wiz-text-1)] transition-colors"
          >
            <Smartphone size={13} />
            {t("signup.trust.continueDevice", "Continue on another device")}
          </button>
          <span className="hidden sm:inline-block h-1 w-1 rounded-full" style={{ background: "var(--wiz-line-strong)" }} />
          <span className="font-medium">
            {t("signup.trust.haveAccount", "Already have an account?")}{" "}
            <a
              href="https://live.ofinancial.markets/login"
              className="text-[color:var(--brand)] font-semibold underline underline-offset-2 hover:text-[color:var(--wiz-text-1)]"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("signup.trust.logIn", "Log in")}
            </a>
          </span>
        </div>
      </div>

      {/* Cross-device share modal · resume URL + clipboard-copy. */}
      {shareModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={() => setShareModalOpen(false)}
          data-testid="signup-share-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full rounded-2xl p-6"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--wiz-line)",
              boxShadow: "0 24px 60px -12px rgba(0,0,0,0.5)",
            }}
            data-testid="signup-share-modal"
          >
            <div
              className="h-11 w-11 rounded-full grid place-items-center mb-4"
              style={{
                background: "color-mix(in srgb, var(--brand) 14%, transparent)",
                border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
              }}
            >
              <Smartphone size={18} className="text-[color:var(--brand)]" />
            </div>
            <h2 className="text-[18px] font-semibold tracking-tight text-[color:var(--wiz-text-1)] leading-tight">
              {t("signup.share.title", "Resume on another device")}
            </h2>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-[color:var(--wiz-text-2)]">
              {t("signup.share.body", "Open this link on your phone, tablet, or laptop to pick up exactly where you left off. The link expires in 24 hours.")}
            </p>
            <div
              className="mt-4 rounded-lg p-3 text-[12px] font-mono break-all text-[color:var(--wiz-text-1)]"
              style={{
                background: "color-mix(in srgb, var(--wiz-text-1) 5%, transparent)",
                border: "1px solid var(--wiz-line)",
              }}
              data-testid="signup-share-url"
            >
              {shareUrl}
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setShareModalOpen(false)}
                data-testid="signup-share-close"
                className="text-[color:var(--wiz-text-2)] hover:text-[color:var(--wiz-text-1)]"
              >
                {t("signup.share.close", "Done")}
              </Button>
              <Button
                onClick={copyShareUrl}
                data-testid="signup-share-copy"
                className="gap-2 font-semibold"
              >
                <Copy size={14} /> {t("signup.share.copy", "Copy link")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );

  if (compact) return wizardBody;
  return (
    <div className="relative overflow-x-hidden" data-testid="signup-wizard-page">
      <Navbar />
      {wizardBody}
      <Footer />
    </div>
  );
}
